// pages/admin/PlanManagement.jsx — B1: Per-Seat Pricing Management + F2: Add-on Pricing
import { useState, useEffect } from 'react';
import { adminGetConfig, adminUpdateConfig, adminGetAuditPricing, adminSetAuditPricing } from '../api/kailux';
import AddonPricing from '../components/AddonPricing';
import { T, alpha } from '../theme/theme'


const inp = {
  width:'100%', padding:'8px 12px', background: T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color: T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif", boxSizing:'border-box',
}

const lbl = { fontSize:'10px', fontWeight:700, color: T.lt, textTransform:'uppercase',
  letterSpacing:'0.08em', display:'block', marginBottom:'5px' }

// Shown (and written on first edit) when the stored config has no Review tiers yet; the backend
// default in pricing_config.REVIEW_FEE_TIERS is identical.
const DEFAULT_REVIEW_TIERS = [
  { min: 1, max: 1, rate: 450 },
  { min: 2, max: 4, rate: 400 },
  { min: 5, max: 9, rate: 350 },
  { min: 10, max: null, rate: 300 },
]

function tierLabel(tier) {
  if (tier.max === null || tier.max === undefined) return `${tier.min}+ users`
  if (tier.min === tier.max) return `${tier.min} user${tier.min>1?'s':''}`
  return `${tier.min}–${tier.max} users`
}

function TierTable({ title, hint, tiers, onChange }) {
  return (
    <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
      <div style={{ fontSize:'14px', fontWeight:700, color:T.tx, marginBottom:'2px' }}>{title}</div>
      <div style={{ fontSize:'12px', color:T.mt, marginBottom:'12px' }}>{hint}</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {tiers.map((tier, i) => (
          <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'10px', alignItems:'center' }}>
            <span style={{ fontSize:'13px', color:T.lt }}>{tierLabel(tier)}</span>
            <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
              <span style={{ fontSize:'13px', color:T.mt }}>R</span>
              <input type="number" style={{ ...inp, width:'90px' }} value={tier.rate}
                onChange={e => {
                  const next = tiers.map((t, ti) => ti === i ? { ...t, rate: Number(e.target.value) } : t)
                  onChange(next)
                }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SeatPricingTab() {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [dirty,   setDirty]   = useState(false);
  const [toast,   setToast]   = useState(null);
  // Audit credit price lives with the audit module (empty = not for sale); edited here with the rest.
  const [auditPrice, setAuditPrice] = useState('');
  const [auditPriceDirty, setAuditPriceDirty] = useState(false);

  useEffect(() => {
    adminGetAuditPricing()
      .then(d => setAuditPrice(d?.price_per_credit_rand ?? ''))
      .catch(() => {});
    adminGetConfig('seat_pricing')
      .then(d => { setConfig(d.config_value); setLoading(false); })
      .catch(() => { setLoading(false); showToast('Failed to load pricing config','error'); });
  }, []);

  function showToast(msg, type='success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function update(field, value) {
    setConfig(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await adminUpdateConfig('seat_pricing', config);
      if (auditPriceDirty && Number(auditPrice) > 0) {
        await adminSetAuditPricing(Number(auditPrice));
      }
      setAuditPriceDirty(false);
      setDirty(false);
      showToast('Pricing saved — live immediately, no redeploy needed');
    } catch { showToast('Save failed', 'error'); }
    finally { setSaving(false); }
  }

  if (loading) return (
    <div style={{height:'200px',display:'flex',alignItems:'center',justifyContent:'center',color:T.mt}}>Loading pricing…</div>
  );
  if (!config) return (
    <div style={{color:T.rd,fontSize:'14px'}}>Could not load seat_pricing config.</div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'20px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <h3 style={{fontSize:'15px',fontWeight:700,color:T.tx,margin:0}}>Per-seat pricing</h3>
          <p style={{fontSize:'13px',color:T.lt,marginTop:'3px'}}>
            One plan, every feature, priced per user. Changes take effect immediately.
          </p>
        </div>
        <button onClick={handleSave} disabled={!dirty||saving}
          style={{padding:'8px 18px',borderRadius:'8px',border:'none',cursor:dirty?'pointer':'not-allowed',
            fontFamily:"'Outfit',sans-serif",fontSize:'14px',fontWeight:700,transition:'all 0.15s',
            background:dirty? T.prg : T.s2, color:dirty?T.onpri:T.mt, opacity:saving?0.6:1}}>
          {saving?'Saving…':dirty?'● Save Changes':'No Changes'}
        </button>
      </div>

      {toast && (
        <div style={{padding:'10px 14px',borderRadius:'8px',fontSize:'14px',fontWeight:600,
          background:toast.type==='error'?T.rdd:T.gnd,
          color:toast.type==='error'?T.rd:T.gn,
          border:`1px solid ${toast.type==='error'?alpha(T.rd, 0.25):alpha(T.gn, 0.25)}`}}>
          {toast.msg}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
          <label style={lbl}>Seat price (R / user / month)</label>
          <input type="number" style={inp} value={config.seat_price}
            onChange={e => update('seat_price', Number(e.target.value))} />
        </div>
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
          <label style={lbl}>AFS credits per seat / month (pooled)</label>
          <input type="number" style={inp} value={config.afs_credits_per_seat}
            onChange={e => update('afs_credits_per_seat', Number(e.target.value))} />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
        <TierTable
          title="AFS overage (per extra AFS)"
          hint="Charged in arrears on next month's invoice for AFS generated beyond the pooled credits."
          tiers={config.afs_overage_tiers}
          onChange={t => update('afs_overage_tiers', t)}
        />
        <TierTable
          title="Review (per file, subscription firms)"
          hint="Billed in arrears for each review file, once per client and financial year. Annual firms always get the best tier."
          tiers={config.review_fee_tiers ?? DEFAULT_REVIEW_TIERS}
          onChange={t => update('review_fee_tiers', t)}
        />
        <TierTable
          title="Management accounts (per file)"
          hint="Separate product, not part of the AFS pool. Also billed in arrears."
          tiers={config.management_account_tiers}
          onChange={t => update('management_account_tiers', t)}
        />
      </div>

      <div style={{ background:T.ord, border:`1px solid ${alpha(T.or, 0.19)}`, borderRadius:'10px', padding:'12px 16px', fontSize:'12px', color:T.mt }}>
        These figures are launch estimates. Before treating them as final, confirm real Anthropic API
        cost per AFS job and per management-account file, and make sure the lowest overage/management-account
        tier still clears cost with margin. See <code style={{background:T.s3,padding:'1px 5px',borderRadius:'6px',color:T.lt}}>PRICING.md</code> for the full rationale.
      </div>

      <div>
        <h3 style={{fontSize:'15px',fontWeight:700,color:T.tx,margin:'4px 0 3px'}}>Pay-per-engagement pricing</h3>
        <p style={{fontSize:'13px',color:T.lt,marginTop:0,marginBottom:'12px'}}>
          The alternative plan — no seats, no pool, charged per file at the moment of generation.
          Flat rate, no tiers. See <code style={{background:T.s3,padding:'1px 5px',borderRadius:'6px',color:T.lt}}>pricing_config.py</code>.
        </p>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
          <label style={lbl}>Per AFS generated (R)</label>
          <input type="number" style={inp} value={config.pay_per_engagement_afs_price ?? ''}
            onChange={e => update('pay_per_engagement_afs_price', Number(e.target.value))} />
        </div>
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
          <label style={lbl}>Per management accounts file (R)</label>
          <input type="number" style={inp} value={config.pay_per_engagement_mgmt_price ?? ''}
            onChange={e => update('pay_per_engagement_mgmt_price', Number(e.target.value))} />
        </div>
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
          <label style={lbl}>Per review file, on top of the AFS (R)</label>
          <input type="number" style={inp} value={config.pay_per_engagement_review_price ?? ''}
            onChange={e => update('pay_per_engagement_review_price', Number(e.target.value))} />
        </div>
        <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'16px' }}>
          <label style={lbl}>Per audit credit, on top of the AFS (R)</label>
          <input type="number" style={inp} value={auditPrice} placeholder="Not for sale until set"
            onChange={e => { setAuditPrice(e.target.value); setAuditPriceDirty(true); setDirty(true) }} />
        </div>
      </div>
    </div>
  );
}

export default function PlanManagement() {
  const [tab, setTab] = useState('pricing');

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'20px'}}>
      <div>
        <h2 style={{fontSize:'18px',fontWeight:700,color:T.tx,margin:0}}>Plan Management</h2>
        <p style={{fontSize:'14px',fontWeight:'400',color:T.mt,marginTop:'3px'}}>Per-seat pricing and add-ons — all live, no redeploy.</p>
      </div>

      <div style={{ display:'flex', borderBottom:`1px solid ${T.bd}` }}>
        {[{key:'pricing',label:'Seat Pricing'},{key:'addons',label:'Add-ons'}].map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{padding:'9px 18px',fontSize:'14px',fontWeight:600,cursor:'pointer',
              border:'none',background:'transparent',fontFamily:"'Outfit',sans-serif",
              borderBottom:`2px solid ${tab===t.key?T.or:'transparent'}`,
              marginBottom:'-1px',color:tab===t.key?T.or:T.lt,transition:'all 0.15s'}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==='pricing' && <SeatPricingTab />}
      {tab==='addons'  && <AddonPricing />}
    </div>
  );
}
